import { useCallback } from 'react'

import { NavTab } from '@/Atomic/Widgets/DataItemTabs/const'
import { PagePermaLink } from '@/const'
import { FeatureFlag, FeatureFlags } from '@/Data/const'
import { useFeatureFlags } from '@/hooks/useFeatureFlagHandler'

type FeatureFlagChecker = (flag: FeatureFlag) => boolean

export const pageFeatureFlagMap: Partial<Record<PagePermaLink, FeatureFlags>> =
  {
    [PagePermaLink.ACTIVITIES]: FeatureFlags.SHOW_ACTIVITY,
    [PagePermaLink.COMPANIES]: FeatureFlags.SHOW_CONTACT,
    [PagePermaLink.DASHBOARD]: FeatureFlags.SHOW_DASHBOARD,
    [PagePermaLink.DOCUMENTS]: FeatureFlags.SHOW_DOCUMENT,
    [PagePermaLink.LEADS]: FeatureFlags.SHOW_LEAD,
    [PagePermaLink.LISTS]: FeatureFlags.SHOW_LIST,
    [PagePermaLink.MY_WORK]: FeatureFlags.SHOW_MY_WORK,
    [PagePermaLink.MY_CONTACTS]: FeatureFlags.SHOW_MY_WORK,
    [PagePermaLink.OPPORTUNITIES]: FeatureFlags.SHOW_OPPORTUNITY,
    [PagePermaLink.ORDERS]: FeatureFlags.SHOW_ORDER,
    [PagePermaLink.PERSONS]: FeatureFlags.SHOW_CONTACT,
    [PagePermaLink.QUOTES]: FeatureFlags.SHOW_QUOTE,
    [PagePermaLink.TICKETS]: FeatureFlags.SHOW_TICKET,
    [PagePermaLink.TIME_TRACKING]: FeatureFlags.SHOW_TIME_TRACKING,
  }

const navTabFeatureFlagMap: Partial<Record<NavTab, FeatureFlags>> = {
  [NavTab.ACTIVITY]: FeatureFlags.SHOW_ACTIVITY,
  [NavTab.ADDRESS]: FeatureFlags.SHOW_ADDRESS,
  [NavTab.COMPANY]: FeatureFlags.SHOW_CONTACT,
  [NavTab.CONTACT]: FeatureFlags.SHOW_CONTACT,
  [NavTab.DOCUMENT]: FeatureFlags.SHOW_DOCUMENT,
  [NavTab.EXTERNAL_COMPANY]: FeatureFlags.SHOW_CONTACT,
  [NavTab.EXTERNAL_PERSON]: FeatureFlags.SHOW_CONTACT,
  [NavTab.FOLLOW_UPS]: FeatureFlags.SHOW_ACTIVITY,
  [NavTab.INTERNAL_COMPANY]: FeatureFlags.SHOW_CONTACT,
  [NavTab.INTERNAL_PERSON]: FeatureFlags.SHOW_CONTACT,
  [NavTab.INVOICE]: FeatureFlags.SHOW_INVOICE,
  [NavTab.MESSAGE]: FeatureFlags.SHOW_MESSAGE,
  [NavTab.OPPORTUNITY]: FeatureFlags.SHOW_OPPORTUNITY,
  [NavTab.ORDER]: FeatureFlags.SHOW_ORDER,
  [NavTab.PERSON]: FeatureFlags.SHOW_CONTACT,
  [NavTab.QUOTE]: FeatureFlags.SHOW_QUOTE,
  [NavTab.TICKET]: FeatureFlags.SHOW_TICKET,
}

export const isPageEnabled = (
  permalink: string | undefined | null,
  isFeatureFlagEnabled: FeatureFlagChecker,
) => {
  if (!permalink) return true

  const flag = pageFeatureFlagMap[permalink as PagePermaLink]
  if (!flag) return true

  return isFeatureFlagEnabled(flag)
}

export const filterTabsByFeatureFlags = (
  tabs: NavTab[],
  isFeatureFlagEnabled: FeatureFlagChecker,
) =>
  tabs.filter((tab) => {
    const flag = navTabFeatureFlagMap[tab]
    if (!flag) return true
    return isFeatureFlagEnabled(flag)
  })

export const useFeatureFlagAccess = () => {
  const { isEnabled } = useFeatureFlags()

  const isPageEnabledWithContext = useCallback(
    (permalink?: string | null) => isPageEnabled(permalink, isEnabled),
    [isEnabled],
  )

  const filterTabsWithContext = useCallback(
    (tabs: NavTab[]) => filterTabsByFeatureFlags(tabs, isEnabled),
    [isEnabled],
  )

  return {
    filterTabs: filterTabsWithContext,
    isFeatureFlagEnabled: isEnabled,
    isPageEnabled: isPageEnabledWithContext,
  }
}
